import { GQLResponseError } from '@change-corgi/core/gql';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { PetitionPageVideos, type PetitionPageVideosQuery, type PetitionPageVideosQueryVariables } from './api.graphql';

export type SupporterVoicesVideos = {
	enabled: boolean;
	videos: PetitionPageVideosQuery['petitionVideosConnection']['nodes'];
	totalCount: number;
};

export const getSupporterVoicesVideos = async (
	utilityContext: UtilityContext,
	slugOrId: string,
	disableDisplay: boolean,
): Promise<SupporterVoicesVideos> => {
	/**
	 * Moderation staff can disable display of videos using petition metadata, in which case we
	 * should not fetch them at all.
	 */
	if (disableDisplay) {
		return {
			enabled: false,
			videos: [],
			totalCount: 0,
		};
	}

	const {
		gql: { fetch },
		errorReporter: { report },
	} = utilityContext;

	// supporter-voices videos are a non-critical section of the petition details page — a
	// failure here must not take down the whole page, so we catch and fall back to an empty
	// list rather than letting the error propagate and break page rendering.
	try {
		const { data } = await fetch<PetitionPageVideosQuery, PetitionPageVideosQueryVariables>({
			query: PetitionPageVideos,
			variables: {
				petitionSlugOrId: slugOrId,
				first: 10,
				sortBy: 'SCORE',
			},
			rejectOnError: true,
			batched: true,
		});

		return {
			enabled: true,
			videos: data.petitionVideosConnection.nodes,
			totalCount: data.petitionVideosConnection.totalCount,
		};
	} catch (error) {
		void report(
			{
				error: new Error('Failed to load supporter voices videos', { cause: error }),
				context: {
					requestId: error instanceof GQLResponseError ? error.requestId : undefined,
				},
				params: {
					slugOrId,
					...(error instanceof GQLResponseError ? { errors: error.errors } : undefined),
				},
			},
			{ severity: 'warning' },
		);

		return {
			enabled: true,
			videos: [],
			totalCount: 0,
		};
	}
};
